import { useCallback, useEffect, useState } from "react";
import { AnimatePresence, motion } from "@/motion/appMotion";
import { CheckCircle2, AlertCircle, X } from "lucide-react";

const TOAST_EVENT = "jobscribe:toast";

let toastId = 0;

function emitToast(detail) {
  if (typeof window === "undefined") return;
  window.dispatchEvent(new CustomEvent(TOAST_EVENT, { detail }));
}

export function showSuccessToast(message, title = "Success") {
  emitToast({ type: "success", message, title });
}

export function showErrorToast(message, title = "Error") {
  emitToast({ type: "error", message, title });
}

export default function ToastHost() {
  const [toasts, setToasts] = useState([]);

  const dismiss = useCallback((id) => {
    setToasts((prev) => prev.filter((t) => t.id !== id));
  }, []);

  useEffect(() => {
    const onToast = (event) => {
      const detail = event.detail || {};
      const id = ++toastId;
      const entry = {
        id,
        type: detail.type === "error" ? "error" : "success",
        title: detail.title || (detail.type === "error" ? "Error" : "Success"),
        message: detail.message || "",
      };
      setToasts((prev) => [...prev, entry].slice(-4));
      window.setTimeout(() => dismiss(id), detail.duration ?? 4500);
    };

    window.addEventListener(TOAST_EVENT, onToast);
    return () => window.removeEventListener(TOAST_EVENT, onToast);
  }, [dismiss]);

  return (
    <div
      className="pointer-events-none fixed inset-x-0 top-4 z-[100] flex flex-col items-end gap-2 px-4 sm:top-6 sm:px-6"
      aria-live="polite"
      aria-atomic="true"
    >
      <AnimatePresence>
        {toasts.map((toast) => (
          <motion.div
            key={toast.id}
            initial={{ opacity: 0, y: -12, scale: 0.96 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: -8, scale: 0.96 }}
            transition={{ duration: 0.22, ease: "easeOut" }}
            className={`pointer-events-auto w-full max-w-sm overflow-hidden rounded-2xl border shadow-xl backdrop-blur-sm ${
              toast.type === "success"
                ? "border-emerald-200/80 bg-white/95"
                : "border-rose-200/80 bg-white/95"
            }`}
          >
            <div className="flex items-start gap-3 p-4">
              <div
                className={`mt-0.5 flex h-9 w-9 shrink-0 items-center justify-center rounded-xl ${
                  toast.type === "success"
                    ? "bg-emerald-100 text-emerald-600"
                    : "bg-rose-100 text-rose-600"
                }`}
              >
                {toast.type === "success" ? (
                  <CheckCircle2 className="h-5 w-5" strokeWidth={2.25} />
                ) : (
                  <AlertCircle className="h-5 w-5" strokeWidth={2.25} />
                )}
              </div>
              <div className="min-w-0 flex-1 pr-1">
                <p
                  className="text-sm font-bold text-slate-900"
                  style={{ fontFamily: "Parkinsans, sans-serif" }}
                >
                  {toast.title}
                </p>
                <p
                  className="mt-0.5 text-sm leading-relaxed text-slate-600"
                  style={{ fontFamily: "Parkinsans, sans-serif" }}
                >
                  {toast.message}
                </p>
              </div>
              <button
                type="button"
                onClick={() => dismiss(toast.id)}
                className="shrink-0 rounded-lg p-1 text-slate-400 transition-colors hover:bg-slate-100 hover:text-slate-600"
                aria-label="Dismiss notification"
              >
                <X className="h-4 w-4" />
              </button>
            </div>
          </motion.div>
        ))}
      </AnimatePresence>
    </div>
  );
}
